import { Box, Flex, Grid, GridItem, Icon } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { FarmTeaserFragment } from '../../generated/types';
import { ImageSlider } from '../Common/ImageSlider';
import { IoMdPin } from '@react-icons/all-files/io/IoMdPin';
import Link from 'next/link';
import { IoChevronForward } from '@react-icons/all-files/io5/IoChevronForward';
import { Rating } from '../Common/Rating';
import { FarmCategoryList } from '../Node/Farm/FarmCategoryList';
import { ImageWithDerivative } from '../../types/global';

interface FarmTeaserProps {
  data: FarmTeaserFragment;
}

export const FarmTeaser: FC<FarmTeaserProps> = ({ data }) => {
  const images = useMemo<ImageWithDerivative[]>(() => {
    const images: ImageWithDerivative[] = [];

    if (data.teaserImage) {
      images.push(data.teaserImage);
    }

    data.images?.forEach((image) => images.push(image));

    return images;
  }, [data]);

  const isPremium = data.listingType.title === 'Premium';
  const isAddress = data.listingType.title === 'Address';

  return (
    <>
      <Grid
        templateColumns="repeat(12, 1fr)"
        templateRows={{
          base: 'repeat(1, 1fr)',
          md: 'auto 1fr',
        }}
        columnGap={{
          base: isPremium ? 0 : 3,
          md: 6,
        }}
        rowGap={{
          base: 0,
          md: 0,
        }}
        width="100%"
      >
        <GridItem
          rowSpan={2}
          colSpan={{
            base: isPremium ? 12 : 4,
            md: 4,
          }}
        >
          <Box pos="relative" overflow="hidden">
            {isPremium && (
              <Box
                textTransform="uppercase"
                pos="absolute"
                px={8}
                left="-38px"
                top="38px"
                zIndex={2}
                bg="secondary"
                color="#fff"
                fontSize="sm"
                fontWeight="bold"
                transform="rotate(-35deg)"
              >
                Trabland Premium
              </Box>
            )}
            <Flex
              justifyContent={{
                base: 'center',
                md: 'flex-end',
              }}
            >
              <ImageSlider
                maxW={{
                  base: '100%',
                  md: !isPremium ? '60%' : '100%',
                }}
                images={images}
                url={data.url}
                hideCopyright
              />
            </Flex>
          </Box>
        </GridItem>
        <GridItem
          colSpan={{
            base: isPremium ? 12 : 8,
            md: 8,
          }}
        >
          <Link href={data.url} passHref>
            <Box
              display="block"
              as="a"
              fontSize={{
                base: 'lg',
                md: 'xl',
              }}
              fontWeight="bold"
              mt={{
                base: isPremium ? 2 : 0,
                md: 0,
              }}
              color="primary"
              _hover={{
                color: 'primaryDark',
              }}
            >
              {data.title}
            </Box>
          </Link>
          <Box>
            <Flex fontSize="sm" alignItems="center" mt={1}>
              <Icon fontSize="lg" mr={1} as={IoMdPin} color="primary" />
              {data.address.postcode} {data.address.city}
            </Flex>
          </Box>
        </GridItem>
        <GridItem
          colSpan={{
            base: 12,
            md: 8,
          }}
        >
          <Box mt={2}>
            <FarmCategoryList categories={data.categories} />
          </Box>
          <Box mt={2} color="gray.600" fontSize="sm">
            {data.teaserText}
          </Box>
          {data.voteCount > 0 && (
            <Flex alignItems="center" mt={4}>
              <Flex alignItems="center">
                <Rating
                  initialRating={data.voteAverage}
                  readonly
                  iconProps={{
                    fontSize: 'xl',
                  }}
                />
                <Box ml={2} mt="-6px" fontSize="sm">
                  ({data.voteCount} Bewertungen)
                </Box>
              </Flex>
            </Flex>
          )}
          <Link href={data.url} passHref>
            <Box
              width={{
                base: '100%',
                md: 'auto',
              }}
              justifyContent={{
                base: 'center',
                md: 'flex-start',
              }}
              mt={4}
              as="a"
              layerStyle="link_button_blue"
              fontSize={{
                base: 'sm',
                md: 'md',
              }}
            >
              Mehr erfahren
              <Icon ml={1} as={IoChevronForward} />
            </Box>
          </Link>
        </GridItem>
      </Grid>
    </>
  );
};
